import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import LiveTicker from "@/components/LiveTicker";
import CategoryCard from "@/components/CategoryCard";
import MarketCard from "@/components/MarketCard";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { ArrowRight, Shield, Sparkles, Trophy, Eye } from "lucide-react";
import { formatPoints } from "@/lib/format";

export default function Home() {
    const { t } = useI18n();
    const [categories, setCategories] = useState([]);
    const [liveMarkets, setLiveMarkets] = useState([]);
    const [popularMarkets, setPopularMarkets] = useState([]);
    const [stats, setStats] = useState({ users: 0, markets: 0, live_markets: 0, total_pool: 0 });
    const [hero, setHero] = useState({ image_url: "/hero-play2win.png", market: null });

    useEffect(() => {
        (async () => {
            try {
                const [cats, live, pop, st, h] = await Promise.all([
                    api.get("/categories"),
                    api.get("/markets", { params: { is_live: true, limit: 12 } }),
                    api.get("/markets", { params: { is_featured: true, limit: 6 } }),
                    api.get("/stats/overview"),
                    api.get("/hero/config"),
                ]);
                setCategories(cats.data);
                setLiveMarkets(live.data);
                setPopularMarkets(pop.data);
                setStats(st.data);
                setHero(h.data);
            } catch (e) {
                console.error(e);
            }
        })();
    }, []);

    // Resolve the hero market (admin-selected takes priority, falls back to first popular)
    const heroMarket = hero.market || popularMarkets[0] || null;

    // Build full URL for backend-served static assets (e.g. /api/static/hero/xxx.png)
    const heroImageUrl = (() => {
        const u = hero.image_url || "/hero-play2win.png";
        if (u.startsWith("http")) return u;
        if (u.startsWith("/api/")) {
            const base = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, "") || "";
            return `${base}${u}`;
        }
        return u;
    })();

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative overflow-hidden">
                {/* Background image: dark sports composite */}
                <div className="absolute inset-0">
                    {/* Custom AI-generated hero: persona con ítems alusivos a categorías */}
                    {/* Mobile: image fully visible at top (contain). Desktop: cover anchored right. */}
                    <div
                        className="absolute inset-0 bg-no-repeat bg-contain bg-top opacity-[0.75] lg:bg-cover lg:bg-right lg:opacity-[0.7]"
                        style={{
                            backgroundImage: `url('${heroImageUrl}')`,
                        }}
                        aria-hidden
                    />
                    {/* Mobile vignette: lighter top (let image breathe), heavy bottom (text legible) */}
                    <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(10,11,18,0.25)_0%,rgba(10,11,18,0.55)_45%,rgba(10,11,18,0.95)_72%,rgba(10,11,18,1)_100%)] lg:hidden" />
                    {/* Desktop vignette: horizontal — dark on the left so heading is readable */}
                    <div className="absolute inset-0 hidden lg:block bg-[linear-gradient(90deg,rgba(10,11,18,0.95)_0%,rgba(10,11,18,0.72)_38%,rgba(10,11,18,0.18)_72%,rgba(10,11,18,0.7)_100%)]" />
                    <div className="absolute inset-0 hidden lg:block bg-[linear-gradient(180deg,rgba(10,11,18,0.45)_0%,transparent_30%,transparent_70%,rgba(10,11,18,1)_100%)]" />
                </div>

                {/* Existing neon orbs over the photo */}
                <div className="glow-orb bg-p2w-purple w-[500px] h-[500px] -top-40 -left-40" />
                <div className="glow-orb bg-p2w-green w-[400px] h-[400px] top-20 -right-32 opacity-30" />
                <div className="absolute inset-0 bg-grid opacity-30" />

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-20 pb-28 lg:pt-28 lg:pb-36">
                    <div className="grid lg:grid-cols-12 gap-10 items-center">
                        <div className="lg:col-span-7 animate-fade-up">
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-p2w-line bg-p2w-surface/50 backdrop-blur mb-6">
                                <span className="w-1.5 h-1.5 rounded-full bg-p2w-green animate-pulse-dot" />
                                <span className="text-xs uppercase tracking-[0.2em] text-p2w-gray font-medium">
                                    {t("home.hero_eyebrow")}
                                </span>
                            </div>
                            <h1 className="font-sora font-extrabold text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95]">
                                {t("home.hero_title_a")}{" "}
                                <span className="text-gradient-neon">{t("home.hero_title_b")}</span>
                            </h1>
                            <p className="mt-6 text-lg text-p2w-gray max-w-xl leading-relaxed">
                                {t("home.hero_subtitle")}
                            </p>
                            <div className="mt-9 flex flex-wrap items-center gap-3">
                                <Link to="/registro">
                                    <Button
                                        size="lg"
                                        data-testid="hero-cta-primary"
                                        className="bg-p2w-green text-p2w-green-fg hover:bg-p2w-green/90 shadow-glow-green font-semibold text-base px-7 py-6 rounded-xl hover:scale-[1.03] transition-transform"
                                    >
                                        {t("home.cta_primary")} <ArrowRight className="w-4 h-4 ml-2" />
                                    </Button>
                                </Link>
                                <Link to="/mercados">
                                    <Button
                                        size="lg"
                                        variant="outline"
                                        data-testid="hero-cta-secondary"
                                        className="border-p2w-line bg-p2w-surface text-white hover:bg-p2w-elevated hover:text-white px-7 py-6 rounded-xl"
                                    >
                                        {t("home.cta_secondary")}
                                    </Button>
                                </Link>
                            </div>
                            <p className="mt-8 text-xs uppercase tracking-[0.3em] font-sora font-bold text-p2w-green">
                                {t("home.claim")}
                            </p>
                        </div>

                        <div className="lg:col-span-5 animate-fade-up [animation-delay:120ms]">
                            <div className="relative">
                                <div className="absolute inset-0 -m-6 bg-p2w-purple/20 blur-3xl rounded-full" />
                                {(() => {
                                    const card = (
                                        <div className="relative bg-p2w-surface/80 backdrop-blur-xl border border-p2w-line rounded-2xl p-6 shadow-glow-violet transition-all group hover:border-p2w-green/60 hover:shadow-glow-green">
                                    <div className="flex items-center justify-between mb-5">
                                        <div className="flex items-center gap-2">
                                            <span className="w-2 h-2 rounded-full bg-p2w-magenta animate-pulse-dot" />
                                            <span className="text-[10px] uppercase tracking-[0.25em] font-sora font-bold text-p2w-magenta">
                                                {t("market.live")}
                                            </span>
                                        </div>
                                        <span className="text-[10px] uppercase tracking-[0.2em] text-p2w-gray">
                                            Mercado destacado
                                        </span>
                                    </div>
                                    <h3 className="font-sora font-bold text-2xl text-white leading-tight mb-5 group-hover:text-p2w-green transition-colors">
                                        {heroMarket?.title || "¿Bitcoin superará los $100.000 antes de junio?"}
                                    </h3>
                                    {(heroMarket?.options || [
                                        { id: "1", label: "Sí", multiplier: 1.85, current_pool: 5200 },
                                        { id: "2", label: "No", multiplier: 2.45, current_pool: 2100 },
                                    ]).slice(0, 2).map((o, idx) => (
                                        <div
                                            key={o.id || idx}
                                            className={`mb-2.5 px-4 py-3.5 rounded-xl border flex items-center justify-between ${
                                                idx === 0
                                                    ? "border-p2w-green/40 bg-p2w-green/10"
                                                    : "border-p2w-line bg-p2w-elevated"
                                            }`}
                                        >
                                            <span className="font-medium text-white">{o.label}</span>
                                            <span
                                                className={`font-sora font-bold text-xl number-tabular ${
                                                    idx === 0 ? "text-p2w-green" : "text-white"
                                                }`}
                                            >
                                                {(o.multiplier || o.odds || 1.5).toFixed(2)}x
                                            </span>
                                        </div>
                                    ))}
                                    <div className="mt-4 pt-4 border-t border-p2w-line flex items-center justify-between text-xs">
                                        <span className="text-p2w-gray">{t("market.pool")}</span>
                                        <span className="font-sora font-bold text-p2w-green text-base number-tabular">
                                            {formatPoints(heroMarket?.total_pool || 7300)} pts
                                        </span>
                                    </div>
                                    {heroMarket?.id && (
                                        <div className="mt-4 pt-3 border-t border-p2w-line/60 flex items-center justify-between text-xs">
                                            <span className="text-p2w-gray">Toca para apostar</span>
                                            <span className="font-sora font-bold text-p2w-green flex items-center gap-1 group-hover:translate-x-0.5 transition-transform">
                                                Participar <ArrowRight className="w-3.5 h-3.5" />
                                            </span>
                                        </div>
                                    )}
                                        </div>
                                    );
                                    return heroMarket?.id ? (
                                        <Link to={`/mercado/${heroMarket.id}`} data-testid="hero-featured-market-link" className="block">
                                            {card}
                                        </Link>
                                    ) : card;
                                })()}

                                <div className="grid grid-cols-2 gap-3 mt-4">
                                    <div className="bg-p2w-surface border border-p2w-line rounded-xl p-4">
                                        <div className="text-[10px] uppercase tracking-widest text-p2w-gray">
                                            {t("home.stat_pool")}
                                        </div>
                                        <div className="mt-1 font-sora font-bold text-2xl text-p2w-green number-tabular">
                                            {formatPoints(stats.total_pool)}
                                        </div>
                                    </div>
                                    <div className="bg-p2w-surface border border-p2w-line rounded-xl p-4">
                                        <div className="text-[10px] uppercase tracking-widest text-p2w-gray">
                                            {t("home.stat_live")}
                                        </div>
                                        <div className="mt-1 font-sora font-bold text-2xl text-p2w-magenta number-tabular">
                                            {stats.live_markets}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* LIVE TICKER */}
            <LiveTicker markets={liveMarkets} />

            {/* CATEGORIES */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20" data-testid="categories-section">
                <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
                    <div>
                        <h2 className="font-sora font-extrabold text-3xl sm:text-4xl text-white tracking-tight">
                            {t("home.categories_title")}
                        </h2>
                        <p className="mt-2 text-p2w-gray">{t("home.categories_sub")}</p>
                    </div>
                    <Link to="/mercados" className="text-sm text-p2w-green font-semibold hover:underline">
                        {t("home.view_all")} →
                    </Link>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    {categories.map((c) => (
                        <CategoryCard key={c.id} category={c} />
                    ))}
                </div>
            </section>

            {/* LIVE NOW */}
            {liveMarkets.length > 0 && (
                <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12" data-testid="live-section">
                    <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
                        <div>
                            <h2 className="font-sora font-extrabold text-3xl sm:text-4xl text-white tracking-tight flex items-center gap-3">
                                <span className="w-2.5 h-2.5 rounded-full bg-p2w-magenta animate-pulse-dot shadow-glow-magenta" />
                                {t("home.live_title")}
                            </h2>
                            <p className="mt-2 text-p2w-gray">{t("home.live_sub")}</p>
                        </div>
                        <Link to="/en-vivo" className="text-sm text-p2w-green font-semibold hover:underline">
                            {t("home.view_all")} →
                        </Link>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {liveMarkets.slice(0, 6).map((m) => (
                            <MarketCard key={m.id} market={m} />
                        ))}
                    </div>
                </section>
            )}

            {/* POPULAR */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12" data-testid="popular-section">
                <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
                    <div>
                        <h2 className="font-sora font-extrabold text-3xl sm:text-4xl text-white tracking-tight">
                            {t("home.popular_title")}
                        </h2>
                        <p className="mt-2 text-p2w-gray">{t("home.popular_sub")}</p>
                    </div>
                    <Link to="/mercados" className="text-sm text-p2w-green font-semibold hover:underline">
                        {t("home.view_all")} →
                    </Link>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {popularMarkets.slice(0, 6).map((m) => (
                        <MarketCard key={m.id} market={m} />
                    ))}
                </div>
            </section>

            {/* WHY P2W */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20" data-testid="why-section">
                <h2 className="font-sora font-extrabold text-3xl sm:text-4xl text-white text-center mb-3 tracking-tight">
                    {t("home.why_title")}
                </h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
                    {[
                        { icon: Shield, key: 1, color: "#9FE80D" },
                        { icon: Trophy, key: 2, color: "#7C3AED" },
                        { icon: Eye, key: 3, color: "#3B82F6" },
                        { icon: Sparkles, key: 4, color: "#D6249F" },
                    ].map(({ icon: Icon, key, color }) => (
                        <div
                            key={key}
                            className="p-6 rounded-2xl bg-p2w-surface border border-p2w-line hover:border-p2w-green/30 transition"
                        >
                            <div
                                className="w-11 h-11 rounded-xl flex items-center justify-center mb-5"
                                style={{ backgroundColor: `${color}15`, border: `1px solid ${color}40` }}
                            >
                                <Icon className="w-5 h-5" style={{ color }} />
                            </div>
                            <h3 className="font-sora font-bold text-lg text-white mb-2">
                                {t(`home.why_${key}_title`)}
                            </h3>
                            <p className="text-sm text-p2w-gray leading-relaxed">
                                {t(`home.why_${key}_desc`)}
                            </p>
                        </div>
                    ))}
                </div>
            </section>

            {/* BOTTOM CTA */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
                <div className="relative overflow-hidden rounded-3xl border border-p2w-line bg-gradient-to-br from-p2w-surface to-p2w-elevated p-10 md:p-16">
                    <div className="glow-orb bg-p2w-green w-[300px] h-[300px] -bottom-20 -right-20 opacity-30" />
                    <div className="glow-orb bg-p2w-purple w-[300px] h-[300px] -top-20 -left-20 opacity-40" />
                    <div className="relative max-w-2xl">
                        <h2 className="font-sora font-extrabold text-4xl sm:text-5xl text-white tracking-tight leading-tight">
                            Tú analizas, tú decides, <span className="text-p2w-green">tú ganas.</span>
                        </h2>
                        <p className="mt-4 text-p2w-gray text-lg">
                            1.000 puntos de bienvenida y cientos de mercados esperándote.
                        </p>
                        <Link to="/registro">
                            <Button
                                size="lg"
                                data-testid="bottom-cta-register"
                                className="mt-7 bg-p2w-green text-p2w-green-fg hover:bg-p2w-green/90 shadow-glow-green font-semibold text-base px-7 py-6 rounded-xl"
                            >
                                Crear cuenta gratis <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
